import {ExecutionDetail} from "../model/ShippingRequestModel";
import {
  SHIPPING_DETAILS,
  WARNING_STATES,
  EXECUTION_DETAIL_TYPE_CREATE_AND_UPDATE,
  TERMINAL_STATES,
  BOOKING_MODIFICATION_EXECUTION_STATES,
  BOOKING_EXECUTION_TYPE_CONTAINING_PACKAGING_DETAILS,
  NO_POLLING_SHIPPING_REQUEST_STATUSES,
  CONDITIONAL_POLLING_SHIPPING_REQUEST_STATUSES,
  NON_TERMINAL_EXECUTION_STATES,
  UPDATE_POLLING_STATES,
  CONDITIONAL_POLLING_SHIPPING_REQUEST_STATUSES_FOR_LAST_MILE_BOOKING,
  CONDITIONAL_POLLING_SHIPPING_REQUEST_STATUSES_FOR_RACK_BOOKING,
  CONDITIONAL_POLLING_SHIPPING_REQUEST_STATUSES_FOR_LOAD_BOOKING,
  VALID_ON_HOLD_TICKET_PROPERTIES,
  MISSING_ROUTE_TICKET_ID,
  SHIPMENT_REFERENCE_TYPE,
  CONDITIONAL_POLLING_SHIPPING_REQUEST_STATUSES_FOR_WAREHOUSE_BOOKING, 
  DOCUMENT_UPLOAD_DISABLED_STATES,
  CONFLICTING_ROUTES_TICKET_ID,
  MANUAL_HOLD_REASON_CODES,
  MANUAL_RESUME_REASON_CODES,
  SYSTEM_GENERATED_COMBINE_WORKFLOW
} from "./ConfigConstants";
import {ShippingRequestState} from "../pages/config/ShippingRequestState";
import {
    AWS_SCM_LOAD_SHIPPING_REQUEST_CLASS,
    AWS_SCM_SHIPMENT_RACK_BOOKING,
    AWS_SCM_SHIPPING_REQUEST_CLASS,
    AWS_SCM_WAREHOUSE_BOOKING
} from "../pages/config/ShippingRequestClass";
import {ReasonCodesData} from "../pages";

/**
 * This method would be used to fetch BondedFlag from User Space Properties of Shipping Request.
 * @param properties
 */
export function getBondedFlag(properties: any[]): undefined | string {
    return properties.find(property => property?.id?.value === "bondedFlag")?.value?.valueString;
}

/**
 * This method would be used to fetch Shipment Security Level from User Space Properties of Shipping Request.
 * @param properties
 */
export function getShipmentSecurityLevel(properties: any[]): undefined | string {
    return properties?.find(property => property?.id?.value === "ShipmentSecurityLevels")?.value?.valueString;
}

/**
 * This method would be used to fetch List of CommercialInvoice Ids.
 * @param invoices
 */
export function getCommercialInvoiceList(invoices: any[]): string[] {
    return invoices.map(value => value.id.value);
}

/**
 * This method would be used to fetch List of Purchase Order Numbers.
 * @param orderDetails
 */
export function getPurchaseOrderNumberList(orderDetails: any): string[] {
    return orderDetails?.orderIdList?.map((value: any) => value?.id?.value);
}

/**
 * This method would be used to fetch List of Sales Order Numbers from Shipment References.
 * @param shipmentReferences
 */
export function getSalesOrderList(shipmentReferences: any[]): undefined | string[] {
    return getShipmentReferenceValue(SHIPMENT_REFERENCE_TYPE.SALES_ORDER_NUMBER, shipmentReferences);
}

/**
 * This method would be used to fetch List of PackingList Numbers from Shipment References.
 * @param shipmentReferences
 */
export function getPackingListNumberList(shipmentReferences: any[]): undefined | string[] {
    return getShipmentReferenceValue(SHIPMENT_REFERENCE_TYPE.PACKING_LIST_NUMBER, shipmentReferences);
}

/**
 * This method would be used to fetch Carrier Reference Number from Shipment References.
 * @param shipmentReferences
 */
export function getCarrierReferenceNumber(shipmentReferences: any[]): undefined | string[] {
    return getShipmentReferenceValue(SHIPMENT_REFERENCE_TYPE.CARRIER_REFERENCE_NUMBER, shipmentReferences);
}

/**
 * This method would be used to fetch value from Shipment References for a given key.
 * @param shipmentReferences
 * @param key
 */
export function getShipmentReferenceValue(key: string, shipmentReferences: undefined | any[]): undefined | string[] {
  return shipmentReferences?.find(value => value?.id?.value === key)?.value?.filter((val: any) => val && val.valueString).map((val: any) => val.valueString);
}

/**
* This method is used to get the latest s3 uri file from the shipping request response based on communication detail message type and execution states
* @param executionDetails
* @param communicationDetailsMessageType
* @param executionStates
*/
export function fetchLatestS3UriFile(executionDetails: any[], communicationDetailsMessageType: string, executionStates?: any[]): string{
    return fetchLatestExecutionDetails(executionDetails, executionStates)
        ?.communicationDetails
        ?.filter((communicationDetail:any) => communicationDetail?.messageType === communicationDetailsMessageType)
        ?.sort((a: { messageTimeStamp: string; }, b: { messageTimeStamp: string; }) =>  parseInt(b.messageTimeStamp)-parseInt(a.messageTimeStamp))
        [0]?.messageFileId;
}

/**
* This method is used to get the second last s3 uri file from the shipping request response based on communication detail message type and execution states
* @param executionDetails
* @param communicationDetailsMessageType
* @param executionStates
*/
export function fetchSecondLastS3UriFile(executionDetails: any[], communicationDetailsMessageType: string, executionStates?: any[]): string{
    if (executionStates !== undefined) {
        executionDetails = executionDetails?.filter((executionDetail:any) => executionStates.includes(executionDetail?.executionState));
    }
    return executionDetails
        ?.sort((a, b) => (a.entityVersion > b.entityVersion ? -1 : 1))[1]
        ?.communicationDetails
        ?.filter((communicationDetail:any) => communicationDetail?.messageType === communicationDetailsMessageType)
        ?.sort((a: { messageTimeStamp: string; }, b: { messageTimeStamp: string; }) =>  parseInt(b.messageTimeStamp)-parseInt(a.messageTimeStamp))
        [0]?.messageFileId;
}

export function fetchLatestCreateOrUpdateS3UriFile(executionDetails: any[], properties: any[], communicationDetailsMessageType: string, executionStates?: any[]): string{
    if (executionStates !== undefined) {
        executionDetails = executionDetails?.filter((executionDetail:any) => executionStates.includes(executionDetail?.executionState));
    }
    executionDetails = isBookingRequest(properties) ?
        executionDetails?.filter((executionDetail:any) => BOOKING_EXECUTION_TYPE_CONTAINING_PACKAGING_DETAILS.includes(executionDetail?.executionType)) :
            executionDetails?.filter((executionDetail:any) => EXECUTION_DETAIL_TYPE_CREATE_AND_UPDATE.includes(executionDetail?.executionType));

    return executionDetails
        ?.sort((a, b) => (a.entityVersion > b.entityVersion ? -1 : 1))[0]
        ?.communicationDetails
        ?.filter((communicationDetail:any) => communicationDetail?.messageType === communicationDetailsMessageType)
        ?.sort((a: { messageTimeStamp: string; }, b: { messageTimeStamp: string; }) =>  parseInt(b.messageTimeStamp)-parseInt(a.messageTimeStamp))
        [0]?.messageFileId;
}

export function getCommunicationType(shippingRequest: any) {
    return shippingRequest
        ?.properties
        ?.filter((property:any) => property?.id?.value === SHIPPING_DETAILS.PACKAGING_REFERENCE_TYPE)[0]
        ?.value
        ?.valueString;
}

export function checkValidationWarnings(executionDetail: any) {
    return WARNING_STATES.includes(executionDetail?.executionState);
}

export function isTerminalState(executionDetails: any[], isBooking: boolean) {
    return isBooking ? BOOKING_MODIFICATION_EXECUTION_STATES.includes(fetchLatestExecutionDetails(executionDetails)?.executionState) :
        TERMINAL_STATES.includes(fetchLatestExecutionDetails(executionDetails)?.executionState);
}

export function fetchLatestExecutionDetails(executionDetails: any[], executionStates?: any[], executionTypes?: any[]): any{
    return fetchExecutionDetails(executionDetails, executionStates, executionTypes)
        ?.sort((a: any, b: any) => (a.entityVersion > b.entityVersion ? -1 : 1))[0];
}

export function fetchExecutionDetails(executionDetails: any[], executionStates?: any[], executionTypes?: any[]): any{
    if (executionStates !== undefined) {
        executionDetails = executionDetails?.filter((executionDetail:any) => executionStates.includes(executionDetail?.executionState));
    }
    if (executionTypes !== undefined) {
        executionDetails = executionDetails?.filter((executionDetail:any) => executionTypes.includes(executionDetail?.executionType));
    }
    return executionDetails;
}

export function getExecutionDetailTime(executionDetail: ExecutionDetail): number | undefined {
  const validTimestamps = executionDetail.communicationDetails
    ?.map(communicationDetail => parseInt(communicationDetail.messageTimeStamp))
    .filter(timestamp => !isNaN(timestamp))
    ?? [];

  return validTimestamps.length > 0 ? Math.min(...validTimestamps) : undefined;
}

export function propertyMatch(properties: any[], propertyName: string, propertyValue: string) {
    return properties?.find((property:any) => property?.id?.value === propertyName)?.value?.valueString === propertyValue;
}

export function propertyExists(properties: any[], propertyName: string,) {
    return properties?.some((property:any) => property?.id?.value === propertyName)
}

export function findPropertyValue(properties: any[], propertyName: string) {
    return properties?.find((property:any) => property?.id?.value === propertyName)?.value?.valueString;
}

export function propertyIncludes(properties: any[], propertyName: string, propertyValues: any[]) {
    return propertyValues.includes(properties?.find((property:any) => property?.id?.value === propertyName)?.value?.valueString);
}

export function getOnHoldTicketLink(properties: any[]) {
    const existingTicketIds = VALID_ON_HOLD_TICKET_PROPERTIES.map(p => findPropertyValue(properties, p)).filter(id => id !== undefined)
                                .flatMap(id => id.includes(',') ? id.split(',') : id).map(str => str.trim());
    return existingTicketIds;
}

export function checkOnHoldTicketExists(properties: any[]) {
    const existingTicketProperties = VALID_ON_HOLD_TICKET_PROPERTIES.filter(p => propertyExists(properties, p));
    return existingTicketProperties.length > 0;
}

export function getStateReasonCodes(shippingRequest: any, stateName: string): string[] {
    const reasonCodes = shippingRequest?.stateMetadataList?.find((property:any) => property?.id?.value === stateName)?.reasonCodes;
    return reasonCodes ? reasonCodes.map((c: { valueString: any; }) => c.valueString) : [];
}

export function getOnHoldStateReasonCode(shippingRequest: any): string[] {
    const reasonCodes = shippingRequest?.stateMetadataList?.find((property:any) => property?.id?.value === "OnHold")?.reasonCodes;
    return reasonCodes ? reasonCodes.map((c: { valueString: any; }) => c.valueString) : [];
}

export function getReasonCodeInfo(state: string, label: string, reasonCodesData?: ReasonCodesData[]): { value: string, category: string } | undefined {
    if (!reasonCodesData) return undefined;
    for (const rc of reasonCodesData) {
        if (rc.label === label || rc.value === label) {
            const meta = rc.metadata?.find(m => m.state === state);
            if (meta) return { value: rc.label, category: meta.category };
        }
    }
    return undefined;
}

export function getReasonCodeValue(label: string, reasonCodesData?: ReasonCodesData[]): string {
    return reasonCodesData?.find(rc => rc.label === label || rc.value === label)?.label ?? label;
}

export function isConflictingRoutes(properties: any[]) {
  return propertyExists(properties, CONFLICTING_ROUTES_TICKET_ID);
}

export function isRouteDetailsAbsent(properties: any[]) {
    return propertyExists(properties, MISSING_ROUTE_TICKET_ID) || propertyExists(properties, CONFLICTING_ROUTES_TICKET_ID);
}

export function isRequestModeUI(properties: any[]) {
   return isUIAsnRequest(properties) || isUIBookingRequest(properties);
}

export function isEditASNAllowed(shippingRequest: any) {
  const shippingReqtState = ShippingRequestState.getShippingRequestState(shippingRequest.state.name);

  return isRequestModeUI(shippingRequest.properties) ||
    (isBookingRequest(shippingRequest.properties) && (shippingReqtState?.isUpdateAllowed || shippingReqtState?.isUpdateAllowedForLastMile));
}

export function isUIBookingRequest(properties: any[]) {
   return propertyMatch(properties, SHIPPING_DETAILS.PACKAGING_REFERENCE_TYPE, SHIPPING_DETAILS.BOOKING_COMMUNICATION_DETAIL_PACKAGING_REFERENCE);
}

export function isUIAsnRequest(properties: any[]) {
   return propertyMatch(properties, SHIPPING_DETAILS.PACKAGING_REFERENCE_TYPE, SHIPPING_DETAILS.ASN_COMMUNICATION_DETAIL_PACKAGING_REFERENCE);
}

export function isBookingRequest(properties: any[]) {
   return propertyIncludes(properties, SHIPPING_DETAILS.PACKAGING_REFERENCE_TYPE, SHIPPING_DETAILS.BOOKING_PACKAGING_REFERENCE_STATES);
}

export function isExecutionOrderBookingRequest(properties: any[]) {
    return propertyIncludes(properties, SHIPPING_DETAILS.PACKAGING_REFERENCE_TYPE, SHIPPING_DETAILS.EXECUTION_ORDER_PACKAGING_REFERENCE_STATES) && 
        propertyMatch(properties, SHIPPING_DETAILS.PROPERTY_IS_TMS_ONBOARDED, "true");
 }

export function isChildBookingRequest(shippingRequest: any) {
    return shippingRequest?.classId?.id?.value === AWS_SCM_LOAD_SHIPPING_REQUEST_CLASS;
}

export function isParentBookingRequest(shippingRequest: any) {
    return shippingRequest?.classId?.id?.value === AWS_SCM_SHIPPING_REQUEST_CLASS || 
        (shippingRequest?.classId?.id?.value === AWS_SCM_SHIPMENT_RACK_BOOKING && 
        propertyMatch(shippingRequest?.properties, SHIPPING_DETAILS.PROPERTY_IS_TMS_ONBOARDED, "true"));
}

export function isMultiLegBookingRequest(shippingRequest: any) {
    return isNonRackMultiLegBookingRequest(shippingRequest) || isMultilegRackBookingRequest(shippingRequest);
}

export function isNonRackMultiLegBookingRequest(shippingRequest: any) {
    return shippingRequest?.classId?.id?.value === AWS_SCM_SHIPPING_REQUEST_CLASS;
}

export function isRackBookingRequest(shippingRequest: any) {
    return shippingRequest?.classId?.id?.value === AWS_SCM_SHIPMENT_RACK_BOOKING
}

export function isMultilegRackBookingRequest(shippingRequest: any) : boolean {
    return (shippingRequest?.classId?.id?.value === AWS_SCM_SHIPMENT_RACK_BOOKING &&
        propertyMatch(shippingRequest?.properties, SHIPPING_DETAILS.PROPERTY_IS_TMS_ONBOARDED, "true"));
}

export function isWarehouseBookingRequest(shippingRequest: any) {
    return shippingRequest?.classId?.id?.value === AWS_SCM_WAREHOUSE_BOOKING;
}

export function isMigratedRackBooking(shippingRequest: any) {
    return (isRackBookingRequest(shippingRequest) || isChildBookingRequest(shippingRequest) ||
        isWarehouseBookingRequest(shippingRequest)) && propertyIncludes(
            shippingRequest?.properties, SHIPPING_DETAILS.PROPERTY_MIGRATION_TYPE, SHIPPING_DETAILS.MIGRATION_TYPE_PROPERTIES);
}

export function isAsnRequest(properties: any[]) {
   return propertyIncludes(properties, SHIPPING_DETAILS.PACKAGING_REFERENCE_TYPE, SHIPPING_DETAILS.ASN_PACKAGING_REFERENCE_STATES);
}

export function isCancelExecutionType(executionDetail: any) {
   return executionDetail?.executionType === SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_CANCEL;
}

export function isCreateExecutionType(executionDetail: any) {
   return executionDetail?.executionType === SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_CREATE;
}

export function isAsnUploadExecutionType(executionDetail: any) {
  return executionDetail?.executionType === SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_ASN_UPLOAD;
}

export function isShipmentExpedited(executionDetails: any[]) {
    return executionDetails?.filter((executionDetail:any) =>
        executionDetail?.updateParameters?.serviceLevel === SHIPPING_DETAILS.SERVICE_LEVEL_EXPRESS).length > 0;
}

export function isUpdateExecutionType(executionDetails: any[]) {
  return executionDetails?.filter((executionDetail:any) =>
    executionDetail?.executionType === SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_UPDATE).length > 0;
}

export function isTerminalExecutionState(executionDetail: any) {
   return TERMINAL_STATES.includes(executionDetail?.executionState);
}

export function isBookingModificationExecutionState(executionDetail: any) {
   return BOOKING_MODIFICATION_EXECUTION_STATES.includes(executionDetail?.executionState);
}

export function isOnlyShipmentStatusUpdateRequest(shippingRequest: any) {
   return shippingRequest?.lifeCycleGraphId === SHIPPING_DETAILS.SHIPMENT_STATUS_LIFE_CYCLE_GRAPH_ID ||
     (propertyMatch(shippingRequest?.properties, SHIPPING_DETAILS.PROPERTY_IS_ASN_RECEIVED, "false") &&
      propertyMatch(shippingRequest?.properties, SHIPPING_DETAILS.PROPERTY_IS_SHIPMENT_STATUS_UPDATE_RECEIVED, "true"));
}

export function isAsnShipment(shippingRequest: any) {
    return shippingRequest?.lifeCycleGraphId === SHIPPING_DETAILS.SHIPMENT_ASN_LIFE_CYCLE_GRAPH_ID || (
      shippingRequest?.lifeCycleGraphId === SHIPPING_DETAILS.SHIPMENT_STATUS_LIFE_CYCLE_GRAPH_ID &&
      propertyMatch(shippingRequest?.properties, SHIPPING_DETAILS.PROPERTY_IS_ASN_RECEIVED, "true"));
}

export function isAsnReceivedForShippingRequest(shippingRequest: any) {
    return propertyMatch(shippingRequest?.properties, SHIPPING_DETAILS.PROPERTY_IS_ASN_RECEIVED, "true");
}

export function isAsnNotReceivedForShippingRequest(shippingRequest: any) {
   return propertyMatch(shippingRequest?.properties, SHIPPING_DETAILS.PROPERTY_IS_ASN_RECEIVED, "false");
}

export function isShipmentStatusUpdateRequest(shippingRequest: any) {
   return shippingRequest?.lifeCycleGraphId === SHIPPING_DETAILS.SHIPMENT_STATUS_LIFE_CYCLE_GRAPH_ID ||
        propertyMatch(shippingRequest?.properties, SHIPPING_DETAILS.PROPERTY_IS_SHIPMENT_STATUS_UPDATE_RECEIVED, "true");
}

export function isDocumentUploadAllowedState(shippingRequest: any): boolean {
    const shippingRequestState = ShippingRequestState.getShippingRequestState(shippingRequest?.state?.name);
    return shippingRequestState !== undefined && !DOCUMENT_UPLOAD_DISABLED_STATES.includes(shippingRequestState);
}


/*
 * Below logic stops the poller based on following conditions:
 * 1. If the shipment was created by a ShipmentStatusUpdate, then poller should stop.
 * 2. If Shipment is in state such as Error/Failed/Cancelled/Accepted/BookingRejected, the poller should stop.
 * 3. If Asn Over Booking is in progress or cancel is in progress, then the poller should not stop.
 * 4. If Service level upgrade is in progress and Booking is in BookingAccepted state, then the poller should not stop.
 * 5. If an Update is in progress and Booking is in OnHold state, then the poller should stop.
 * 6. If Booking is in state such as OnHold/BookingRequested/LoadTendered/BookingAccepted, then poller should stop.
 * 7. If Booking is last-mile parent shipment and state is ShipmentPlanned/InProgress, then poller should stop.
 */
export function shouldStopPolling(shippingRequest: any) {
  if (isOnlyShipmentStatusUpdateRequest(shippingRequest)) {
    return true;
  }
  const shippingRequestState = ShippingRequestState.getShippingRequestState(shippingRequest?.state?.name);
  if (shippingRequestState !== undefined && NO_POLLING_SHIPPING_REQUEST_STATUSES.includes(shippingRequestState)) {
    return true;
  }
  if (shippingRequestState !== undefined && isWarehouseBookingRequest(shippingRequest)) {
      return true
  }

  const latestInProgressExecutionForAsnUpload =
    fetchLatestExecutionDetails(shippingRequest.executionDetails, NON_TERMINAL_EXECUTION_STATES,
      [SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_ASN_UPLOAD]);
  const latestInProgressExecutionForCancel =
    fetchLatestExecutionDetails(shippingRequest.executionDetails, NON_TERMINAL_EXECUTION_STATES,
      [SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_CANCEL]);
  if (latestInProgressExecutionForAsnUpload !== undefined || latestInProgressExecutionForCancel !== undefined) {
    return false;
  }
  const latestInProgressExecutionForUpdate =
    fetchLatestExecutionDetails(shippingRequest.executionDetails, NON_TERMINAL_EXECUTION_STATES,
      [SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_UPDATE]);
  if (latestInProgressExecutionForUpdate !== undefined && isAsnShipment(shippingRequest)) {
      return false;
  }
  else if (latestInProgressExecutionForUpdate !== undefined && isShipmentExpedited([latestInProgressExecutionForUpdate])) {
    return ShippingRequestState.BOOKING_ACCEPTED !== shippingRequestState;
  }
  else if (latestInProgressExecutionForUpdate !== undefined && shippingRequestState !== undefined &&
          (latestInProgressExecutionForUpdate.updateParameters?.modeOfTransport && (UPDATE_POLLING_STATES.includes(shippingRequestState)))) {
    return false;
  }
  return shippingRequestState !== undefined && 
    (CONDITIONAL_POLLING_SHIPPING_REQUEST_STATUSES.includes(shippingRequestState) || 
        (isParentBookingRequest(shippingRequest) && CONDITIONAL_POLLING_SHIPPING_REQUEST_STATUSES_FOR_LAST_MILE_BOOKING.includes(shippingRequestState)) ||
        (isRackBookingRequest(shippingRequest) && CONDITIONAL_POLLING_SHIPPING_REQUEST_STATUSES_FOR_RACK_BOOKING.includes(shippingRequestState)) || 
        (isChildBookingRequest(shippingRequest) && CONDITIONAL_POLLING_SHIPPING_REQUEST_STATUSES_FOR_LOAD_BOOKING.includes(shippingRequestState)) ||
        (isWarehouseBookingRequest(shippingRequest) && CONDITIONAL_POLLING_SHIPPING_REQUEST_STATUSES_FOR_WAREHOUSE_BOOKING.includes(shippingRequestState)));
}

export function shouldEnableOrderTracking(shippingRequest: any) {
    const shippingRequestState = ShippingRequestState.getShippingRequestState(shippingRequest?.state?.name);

    return shippingRequestState !== undefined && isParentBookingRequest(shippingRequest) && 
        (CONDITIONAL_POLLING_SHIPPING_REQUEST_STATUSES_FOR_LAST_MILE_BOOKING.includes(shippingRequestState) || 
        (isRackBookingRequest(shippingRequest) && CONDITIONAL_POLLING_SHIPPING_REQUEST_STATUSES_FOR_LOAD_BOOKING.includes(shippingRequestState)));
}

/**
 * Check if execution detail has manual hold reason codes.
 * Uses API reasonCodesData first (checking both label and value), falls back to hardcoded list.
 */
export function hasManualHoldReasonCode(executionDetail: any, reasonCodesData?: ReasonCodesData[]): boolean {
    const reasonCodes = executionDetail?.executionReason?.reasonCodes?.map((code: any) => code.valueString) || [];
    return reasonCodes.some((code: string) => {
        if (reasonCodesData) {
            const match = reasonCodesData.find(rc => rc.label === code || rc.value === code);
            if (match?.metadata?.some(m => m.category === "Manual Hold")) return true;
        }
        return MANUAL_HOLD_REASON_CODES.includes(code);
    });
}

/**
 * Check if execution detail has manual resume reason codes
 * Returns true if there's at least one common element between the two arrays (NOT disjoint)
 * @param executionDetail
 */
export function hasManualResumeReasonCode(executionDetail: any): boolean {
    const reasonCodes = executionDetail?.executionReason?.reasonCodes?.map((code: any) => code.valueString) || [];
    // Check if arrays are NOT disjoint (i.e., they have at least one common element)
    return !reasonCodes.every((code: string) => !MANUAL_RESUME_REASON_CODES.includes(code));
}

/**
 * Check if requestor is system generated combine workflow
 * @param executionDetail
 */
export function isSystemGeneratedCombineWorkflow(executionDetail: any): boolean {
    return executionDetail?.requester?.id?.value === SYSTEM_GENERATED_COMBINE_WORKFLOW;
}

/**
 * Gets the execution detail to display requestor information in Shipping Details Card.
 * 
 * Handles special case: When HOLD/RESUME has SYSTEM_GENERATED_COMBINE_WORKFLOW as requestor,
 * finds the actual manual user who initiated the action.
 * 
 * Logic:
 * - If latest is UPDATE → return it
 * - If latest is HOLD/RESUME with manual reason codes:
 *   - If requestor is system-generated → find first manual requestor from history
 *   - Otherwise → return it as-is
 * - If latest is HOLD/RESUME without manual reason codes → return UPDATE instead
 * 
 * @param shippingRequest The shipping request with executionDetails
 * @return Execution detail for displaying requestor, timestamp, and reason codes
 */
export function getExecutionDetailForRequestorDisplay(shippingRequest: any, reasonCodesData?: ReasonCodesData[]): any {
    // Fetch all relevant execution details (UPDATE, HOLD, RESUME) in a single call
    const allRelevantExecutionDetails = fetchExecutionDetails(
        shippingRequest.executionDetails, 
        undefined, 
        [
            SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_UPDATE,
            SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_HOLD,
            SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_RESUME
        ]
    );
    
    // Find the latest execution detail among UPDATE, HOLD, and RESUME
    const latestExecutionDetail = allRelevantExecutionDetails?.length > 0 
        ? allRelevantExecutionDetails.sort((a: any, b: any) => (a.entityVersion > b.entityVersion ? -1 : 1))[0]
        : undefined;
    
    // Get latest UPDATE for fallback cases
    const latestUpdateExecutionDetail = allRelevantExecutionDetails?.find(
        (detail: any) => detail.executionType === SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_UPDATE
    );
    
    // If latest is UPDATE, return it directly
    if (latestExecutionDetail?.executionType === SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_UPDATE) {
        return latestExecutionDetail;
    }
    
    // Only process HOLD/RESUME if the latest execution detail has manual reason codes
    if (latestExecutionDetail?.executionType === SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_HOLD && 
        hasManualHoldReasonCode(latestExecutionDetail, reasonCodesData)) {
        // If requestor is SYSTEM_GENERATED_COMBINE_WORKFLOW, find the first manual one
        if (isSystemGeneratedCombineWorkflow(latestExecutionDetail)) {
            // Get all HOLD execution details sorted by version (highest to lowest)
            const allHoldDetails = fetchExecutionDetails(shippingRequest.executionDetails, undefined, [SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_HOLD])
                ?.sort((a: any, b: any) => (a.entityVersion > b.entityVersion ? -1 : 1));
            
            // Find the first one with manual reason code and not system generated
            const manualHoldDetail = allHoldDetails?.find((detail: any) => 
                hasManualHoldReasonCode(detail, reasonCodesData) && !isSystemGeneratedCombineWorkflow(detail)
            );
            
            if (manualHoldDetail) return manualHoldDetail;
        }
        // Latest HOLD has manual reason code and non-system requestor, use it
        return latestExecutionDetail;
    } else if (latestExecutionDetail?.executionType === SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_RESUME && 
               hasManualResumeReasonCode(latestExecutionDetail)) {
        // If requestor is SYSTEM_GENERATED_COMBINE_WORKFLOW, find the first manual one
        if (isSystemGeneratedCombineWorkflow(latestExecutionDetail)) {
            // Get all RESUME execution details sorted by version (highest to lowest)
            const allResumeDetails = fetchExecutionDetails(shippingRequest.executionDetails, undefined, [SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_RESUME])
                ?.sort((a: any, b: any) => (a.entityVersion > b.entityVersion ? -1 : 1));
            
            // Find the first one with manual reason code and not system generated
            const manualResumeDetail = allResumeDetails?.find((detail: any) => 
                hasManualResumeReasonCode(detail) && !isSystemGeneratedCombineWorkflow(detail)
            );
            if (manualResumeDetail) return manualResumeDetail;
        }
        // Latest RESUME has manual reason code and non-system requestor, use it
        return latestExecutionDetail;
    }
    
    // If we reach here, latest is HOLD/RESUME without manual reason codes
    // Returning the UPDATE detail instead
    return latestUpdateExecutionDetail;
}
